import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  useGetAdminThreadsQuery,
  useGetAdminTagsQuery,
  usePinThreadMutation,
  useLockThreadMutation,
  useDeleteThreadMutation,
  useUpdateThreadMutation,
  useCreateAnnouncementMutation,
  useCreateThreadMutation,
  useCloseSurveyMutation,
  useUpdateSurveyConfigMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useAppSelector, useAppDispatch } from '@/store';
import { clearDeletedThreads } from '@/store/slices/residents/corkBoardUISlice';
import { useAdminUser } from './useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import type {
  CorkBoardThreadSummary,
  ThreadSortBy,
  CreateAnnouncementRequest,
  CreateSurveyThreadRequest,
  UpdateThreadRequest,
  UpdateSurveyConfigRequest,
} from '@/types/residents/corkBoard';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 30;

export const useThreadModeration = ({ kind }: { kind?: 'thread' | 'survey' } = {}) => {
  const dispatch = useAppDispatch();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const { showSuccess, showError } = useToast();
  const deletedThreadIds = useAppSelector((state) => state.corkBoardUI.deletedThreadIds);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<ThreadSortBy>('newest');
  const [page, setPage] = useState(0);
  const [allThreads, setAllThreads] = useState<CorkBoardThreadSummary[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastSyncedThreadsData, setLastSyncedThreadsData] = useState<unknown>(undefined);
  // True only while a user-initiated pull-to-refresh is in flight. Kept separate
  // from RTK Query's `isFetching` so background refetches (e.g. a pin/lock
  // mutation invalidating the list) don't spin the RefreshControl.
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchInput]);

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
    setAllThreads([]);
    setHasNextPage(false);
  }, [debouncedSearch, sortBy, selectedPropertyId, kind]);

  const {
    data: threadsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAdminThreadsQuery(
    {
      propertyId: selectedPropertyId!,
      search: debouncedSearch || undefined,
      sortBy,
      page,
      pageSize: PAGE_SIZE,
      ...(kind ? { kind } : {}),
    },
    { skip: !selectedPropertyId },
  );

  const { data: tags = [] } = useGetAdminTagsQuery(
    { propertyId: selectedPropertyId!, includeDisabled: false },
    { skip: !selectedPropertyId },
  );

  // Sync during render (not useEffect) so allThreads updates in the same commit RTK
  // exposes new data — avoids a one-frame empty-state flash after isLoading flips false.
  if (threadsData && threadsData !== lastSyncedThreadsData) {
    setLastSyncedThreadsData(threadsData);
    const announcements = threadsData.announcements ?? [];
    const regular = threadsData.threads ?? [];
    const incoming = [...announcements, ...regular];

    if (page === 0) {
      setAllThreads(incoming);
    } else {
      setAllThreads((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const unique = incoming.filter((t) => !existingIds.has(t.id));
        return [...prev, ...unique];
      });
    }

    const { currentPage, totalPages } = threadsData.pagination;
    setHasNextPage(currentPage < totalPages - 1);

    // Fresh data arrived — end any in-flight pull-to-refresh.
    if (isManualRefreshing) setIsManualRefreshing(false);
  }

  const [pinThreadMutation, { isLoading: isPinning }] = usePinThreadMutation();
  const [lockThreadMutation, { isLoading: isLocking }] = useLockThreadMutation();
  const [deleteThreadMutation, { isLoading: isDeleting }] = useDeleteThreadMutation();
  const [updateThreadMutation, { isLoading: isUpdatingTags }] = useUpdateThreadMutation();
  // Separate instance so a full survey edit (title/body/tags) doesn't flip the
  // tags-modal spinner that reads `isUpdatingTags`, and vice versa.
  const [updateSurveyMutation, { isLoading: isUpdatingSurvey }] = useUpdateThreadMutation();
  const [closeSurveyMutation, { isLoading: isClosingSurvey }] = useCloseSurveyMutation();
  const [updateSurveyConfigMutation, { isLoading: isUpdatingSurveyConfig }] = useUpdateSurveyConfigMutation();
  const [createAnnouncementMutation, { isLoading: isCreatingAnnouncement }] = useCreateAnnouncementMutation();
  const [createSurveyMutation, { isLoading: isCreatingSurvey }] = useCreateThreadMutation();

  const isMutating =
    isPinning ||
    isLocking ||
    isDeleting ||
    isUpdatingTags ||
    isUpdatingSurvey ||
    isCreatingAnnouncement ||
    isCreatingSurvey ||
    isClosingSurvey ||
    isUpdatingSurveyConfig;
  const isFetchingNextPage = isFetching && page > 0;

  const visibleThreads = useMemo(() => {
    const base = allThreads.length > 0
      ? allThreads
      : threadsData
        ? [...(threadsData.announcements ?? []), ...(threadsData.threads ?? [])]
        : [];
    return deletedThreadIds.length > 0
      ? base.filter((t) => !deletedThreadIds.includes(String(t.id)))
      : base;
  }, [allThreads, threadsData, deletedThreadIds]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasNextPage) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasNextPage]);

  const handleRefetch = useCallback(async () => {
    dispatch(clearDeletedThreads());
    setIsManualRefreshing(true);

    // If user has scrolled past page 0, just reset args; the query arg change
    // will trigger a fetch and the accumulation effect will repopulate. The
    // render-time data sync clears isManualRefreshing once page-0 data lands.
    if (page !== 0) {
      setPage(0);
      setAllThreads([]);
      setHasNextPage(false);
      return;
    }

    // Already on page 0: force a refetch and populate directly. We can't rely
    // on the accumulation effect because RTK Query may return the same data
    // reference after refetch, leaving [threadsData, page] deps unchanged.
    try {
      const result = await refetch().unwrap();
      const announcements = result.announcements ?? [];
      const regular = result.threads ?? [];
      setAllThreads([...announcements, ...regular]);
      const { currentPage, totalPages } = result.pagination;
      setHasNextPage(currentPage < totalPages - 1);
    } catch {
      // refetch failed — leave the existing list in place
    } finally {
      setIsManualRefreshing(false);
    }
  }, [dispatch, refetch, page]);

  const totalCount = threadsData?.pagination?.totalCount ?? 0;

  const handlePinThread = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      try {
        await pinThreadMutation({ threadId: thread.id, isPinned: !thread.isPinned }).unwrap();
        showSuccess(thread.isPinned ? 'Thread unpinned' : 'Thread pinned');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update pin status');
        return false;
      }
    },
    [pinThreadMutation, showSuccess, showError],
  );

  const handleLockThread = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      try {
        await lockThreadMutation({ threadId: thread.id, isLocked: !thread.isLocked }).unwrap();
        showSuccess(thread.isLocked ? 'Thread unlocked' : 'Thread locked');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update lock status');
        return false;
      }
    },
    [lockThreadMutation, showSuccess, showError],
  );

  const handleDeleteThread = useCallback(
    async (threadId: string) => {
      try {
        await deleteThreadMutation(threadId).unwrap();
        showSuccess('Thread deleted');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to delete thread');
        return false;
      }
    },
    [deleteThreadMutation, showSuccess, showError],
  );

  const handleUpdateThreadTags = useCallback(
    async (threadId: string, tagIds: number[]) => {
      try {
        await updateThreadMutation({ threadId, tagIds }).unwrap();
        showSuccess('Thread tags updated');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update thread tags');
        return false;
      }
    },
    [updateThreadMutation, showSuccess, showError],
  );

  const handleUpdateThread = useCallback(
    async (data: UpdateThreadRequest) => {
      try {
        await updateSurveyMutation(data).unwrap();
        showSuccess('Survey updated');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update survey');
        return false;
      }
    },
    [updateSurveyMutation, showSuccess, showError],
  );

  const handleUpdateSurveyConfig = useCallback(
    async (data: UpdateSurveyConfigRequest) => {
      try {
        await updateSurveyConfigMutation(data).unwrap();
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to update survey settings');
        return false;
      }
    },
    [updateSurveyConfigMutation, showError],
  );

  const handleCloseSurvey = useCallback(
    async (threadId: string) => {
      try {
        await closeSurveyMutation(threadId).unwrap();
        showSuccess('Survey closed');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to close survey');
        return false;
      }
    },
    [closeSurveyMutation, showSuccess, showError],
  );

  const handleCreateAnnouncement = useCallback(
    async (data: Omit<CreateAnnouncementRequest, 'propertyId'>) => {
      if (!selectedPropertyId) return false;
      try {
        await createAnnouncementMutation({ ...data, propertyId: selectedPropertyId }).unwrap();
        showSuccess('Announcement created');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to create announcement');
        return false;
      }
    },
    [selectedPropertyId, createAnnouncementMutation, showSuccess, showError],
  );

  const handleCreateSurvey = useCallback(
    async (data: Omit<CreateSurveyThreadRequest, 'propertyId'>) => {
      if (!selectedPropertyId) return false;
      try {
        await createSurveyMutation({ ...data, propertyId: selectedPropertyId }).unwrap();
        showSuccess('Survey published');
        return true;
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to publish survey');
        return false;
      }
    },
    [selectedPropertyId, createSurveyMutation, showSuccess, showError],
  );

  return {
    threads: visibleThreads,
    tags,
    isLoading,
    isFetching,
    isManualRefreshing,
    isFetchingNextPage,
    isMutating,
    isPinning,
    isLocking,
    isDeleting,
    isUpdatingTags,
    isUpdatingSurvey,
    isCreatingAnnouncement,
    isCreatingSurvey,
    isClosingSurvey,
    isUpdatingSurveyConfig,
    error,
    selectedPropertyId,
    searchInput,
    setSearchInput,
    sortBy,
    setSortBy,
    refetch: handleRefetch,
    hasNextPage,
    loadMore,
    totalCount,
    handlePinThread,
    handleLockThread,
    handleDeleteThread,
    handleUpdateThreadTags,
    handleUpdateThread,
    handleUpdateSurveyConfig,
    handleCloseSurvey,
    handleCreateAnnouncement,
    handleCreateSurvey,
  };
};
